import { useState, type FormEvent } from "react";
import Dropdown from "@common/components/Dropdown";
import IconButton from "@common/components/IconButton";
import type { LogQuery } from "../library/admin";
import { errorText, useAdminLogs } from "../library/queries";

const SOURCES: { value: LogQuery["source"]; label: string }[] = [
  { value: "requests", label: "Requests" },
  { value: "api", label: "API messages" },
];

const RANGES: { value: string; label: string }[] = [
  { value: "15", label: "Last 15 minutes" },
  { value: "60", label: "Last hour" },
  { value: "360", label: "Last 6 hours" },
  { value: "1440", label: "Last day" },
];

const clock = (iso: string) => {
  const at = new Date(iso);
  const today = at.toDateString() === new Date().toDateString();
  return at.toLocaleString(undefined, today ? { timeStyle: "medium" } : { dateStyle: "short", timeStyle: "medium" });
};

/**
 * CloudBoy's logs for admins (GET /admin/logs): its requests (route,
 * status, who) and the API's own messages, newest first.
 */
export default function AdminLogs() {
  const [query, setQuery] = useState<LogQuery>({ source: "requests", minutes: 60, problems: false, q: "" });
  const [text, setText] = useState("");
  const logs = useAdminLogs(query);
  const set = (patch: Partial<LogQuery>) => setQuery((current) => ({ ...current, ...patch }));
  const search = (e: FormEvent) => {
    e.preventDefault();
    set({ q: text.trim() });
  };

  return (
    <section className="panel">
      <div className="section-head">
        <h2>Logs</h2>
        <IconButton icon="refresh" label="Refresh" onClick={() => void logs.refetch()} disabled={logs.isFetching} />
      </div>
      <form className="log-controls" onSubmit={search}>
        <Dropdown value={query.source} options={SOURCES} onChange={(source) => set({ source })} label="Which log" />
        <Dropdown value={String(query.minutes)} options={RANGES} onChange={(minutes) => set({ minutes: Number(minutes) })} label="How far back" />
        <label className="controls-check">
          <input type="checkbox" checked={query.problems} onChange={(e) => set({ problems: e.target.checked })} />
          <span className="muted small">Problems only</span>
        </label>
        <input
          className="input log-search"
          type="search"
          placeholder="Filter, then Enter"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Only lines containing"
        />
      </form>
      {logs.error ? (
        <p className="notice error">{errorText(logs.error, "Could not read the logs.")}</p>
      ) : logs.isLoading ? (
        <p className="muted small">Reading…</p>
      ) : !logs.data?.length ? (
        <p className="muted small">Nothing in that time.</p>
      ) : (
        <>
          <ol className="log-list">
            {logs.data.map((line, i) => (
              <li key={`${line.at}-${i}`} className={`log-line ${line.level}`}>
                <time dateTime={line.at}>{clock(line.at)}</time>
                <span className="log-text">{line.text}</span>
              </li>
            ))}
          </ol>
          {logs.data.length >= 300 && <p className="muted small">The newest 300; narrow the time or filter for more.</p>}
        </>
      )}
    </section>
  );
}
